import Decimal from 'decimal.js/decimal'

import { dateConstants } from '@/modules/base/util/constants'
import { PriceValues } from '@/modules/moduleSubscription/types/module'
import checkDiscount from '@/modules/base/components/modal/upgrade-modal/subscription-selection/_helpers/checkDiscount'
import { MONTHS_PER_YEAR } from '@/modules/moduleSubscription/components/_helpers/calculatePrice'

export interface DiscountSplit {
  discountedMonths: number
  regularMonths: number
  discountedPricePerMonth: number
  regularPricePerMonth: number
}

/**
 * Splits a yearly term into the months covered by a time limited discount and the remaining months
 * at the regular price. `calculatePrice` blends both into a single average price per month, which
 * hides the actual amounts from the customer - use this helper wherever the two amounts should be
 * listed separately.
 *
 * Returns null whenever a single price line already tells the whole story: no discount, discount
 * not applicable for the customer, monthly billing (every invoice is discounted the same way) or a
 * discount covering the complete term. A discount running longer than the billed year counts as
 * covering the complete term - only the first twelve months are priced here.
 */
const getDiscountSplit = (priceValues: PriceValues | null | undefined, period: string, type?: string): DiscountSplit | null => {
  const { pricePerDuration, reducedPrice } = priceValues || {}

  if (!pricePerDuration || !reducedPrice?.percent) return null
  if (period !== dateConstants.YEAR) return null
  if (type && !checkDiscount(type, reducedPrice)) return null

  const { percent, duration = MONTHS_PER_YEAR, period: reducedPeriod } = reducedPrice
  const discountedMonths = reducedPeriod === dateConstants.MONTH || reducedPeriod === dateConstants.FOREVER
    ? duration
    : duration * MONTHS_PER_YEAR

  if (discountedMonths <= 0 || discountedMonths >= MONTHS_PER_YEAR) return null

  const pricePerMonth = new Decimal(pricePerDuration).dividedBy(MONTHS_PER_YEAR)
  const reductionFactor = new Decimal(100).minus(percent).dividedBy(100)

  return {
    discountedMonths,
    regularMonths: MONTHS_PER_YEAR - discountedMonths,
    discountedPricePerMonth: pricePerMonth.times(reductionFactor).toDP(2).toNumber(),
    regularPricePerMonth: pricePerMonth.toDP(2).toNumber(),
  }
}

export default getDiscountSplit
